// Spades — pure rules engine + computer players.
//
// No I/O (it imports only the shared types), so the same file runs in the
// `games` edge function (Deno), in the browser (legal-move hints, card names)
// and under node tests. The GameDef that plugs it into the table framework is
// at the bottom.

import { RuleError, type GameDef, type SeatResult } from './types.ts';
export { RuleError };
//
// Rules (partnership Spades, house defaults):
//  - Seats 0..3 = North, East, South, West. Teams: N/S (0) vs E/W (1).
//  - 13 cards each. Bids 0–13, 0 = nil. No blind nil.
//  - Follow suit if you can. Spades can't be led until broken (a spade was
//    discarded on another suit), unless you hold nothing but spades.
//  - Contract made: 10 × bid + 1 per overtrick (bag). Set: −10 × bid.
//  - Nil: +100 if the nil bidder takes no tricks, −100 if they take any; a
//    failed nil's tricks count as bags (1 point each) for the team.
//  - Every 10 bags: −100 (and the bag count drops by 10).
//  - Game ends after a hand when a team reaches 500 (higher score wins; a
//    tie plays on) or a team falls to −200 (the other team wins).
//  - Dealer rotates clockwise each hand; the player left of the dealer bids
//    and leads first.

export type Card = string; // rank + suit, e.g. 'AS', 'TD', '2C'
export const SUITS = ['C', 'D', 'H', 'S'] as const;
export const RANKS = '23456789TJQKA';
export const SEAT_NAMES = ['North', 'East', 'South', 'West'];
export const TARGET = 500;
export const FLOOR = -200;

export const suitOf = (c: Card) => c[1];
export const rankOf = (c: Card) => RANKS.indexOf(c[0]);
export const teamOf = (seat: number) => seat % 2;
export const partnerOf = (seat: number) => (seat + 2) % 4;
const next = (seat: number) => (seat + 1) % 4;

export interface Play { seat: number; card: Card }
export interface HandResult {
  bids: number[];
  tricks: number[];
  points: [number, number];
  bagsAdded: [number, number];
  bagPenalty: [boolean, boolean];
  nil: { seat: number; made: boolean }[];
}

export interface SpadesState {
  phase: 'bidding' | 'playing' | 'hand_over' | 'game_over';
  handNo: number;
  dealer: number;
  turn: number;
  bids: (number | null)[];
  tricksWon: number[];
  trick: Play[];
  /** The trick just completed — clients show it for a moment. */
  lastTrick: { plays: Play[]; winner: number } | null;
  spadesBroken: boolean;
  /** Every card played this hand (public knowledge; bots use it). */
  played: Card[];
  /** Cards left per seat (public). */
  counts: number[];
  scores: [number, number];
  bags: [number, number];
  lastHand: HandResult | null;
  winner: 0 | 1 | null;
}

export function fullDeck(): Card[] {
  const d: Card[] = [];
  for (const s of SUITS) for (const r of RANKS) d.push(r + s);
  return d;
}

export function shuffle<T>(arr: T[], rng: () => number): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Display order: suits grouped (spades last = rightmost), low to high. */
export function sortHand(hand: Card[]): Card[] {
  const order = { D: 0, C: 1, H: 2, S: 3 } as Record<string, number>;
  return hand.slice().sort((a, b) => order[suitOf(a)] - order[suitOf(b)] || rankOf(a) - rankOf(b));
}

export function newGame(): SpadesState {
  return {
    phase: 'bidding', handNo: 0, dealer: 3, turn: 0,
    bids: [null, null, null, null], tricksWon: [0, 0, 0, 0], trick: [], lastTrick: null,
    spadesBroken: false, played: [], counts: [13, 13, 13, 13],
    scores: [0, 0], bags: [0, 0], lastHand: null, winner: null,
  };
}

/** Deal a hand. `prev` carries scores and the dealer forward. */
export function deal(prev: SpadesState | null, rng: () => number): { state: SpadesState; hands: Card[][] } {
  const base = prev ?? newGame();
  const dealer = prev ? next(prev.dealer) : Math.floor(rng() * 4);
  const deck = shuffle(fullDeck(), rng);
  const hands = [0, 1, 2, 3].map((i) => sortHand(deck.slice(i * 13, i * 13 + 13)));
  return {
    state: {
      ...base,
      phase: 'bidding', handNo: (prev?.handNo ?? 0) + 1, dealer, turn: next(dealer),
      bids: [null, null, null, null], tricksWon: [0, 0, 0, 0], trick: [], lastTrick: null,
      spadesBroken: false, played: [], counts: [13, 13, 13, 13], winner: null,
    },
    hands,
  };
}

export function legalPlays(state: SpadesState, hand: Card[]): Card[] {
  if (state.trick.length === 0) {
    if (state.spadesBroken) return hand.slice();
    const nonSpades = hand.filter((c) => suitOf(c) !== 'S');
    return nonSpades.length ? nonSpades : hand.slice();
  }
  const led = suitOf(state.trick[0].card);
  const follow = hand.filter((c) => suitOf(c) === led);
  return follow.length ? follow : hand.slice();
}

/** Index into `plays` of the card currently winning the trick. */
export function winningIndex(plays: Play[]): number {
  let best = 0;
  for (let i = 1; i < plays.length; i++) {
    const a = plays[best].card, b = plays[i].card;
    const beats = suitOf(b) === suitOf(a) ? rankOf(b) > rankOf(a) : suitOf(b) === 'S';
    if (beats) best = i;
  }
  return best;
}

export function applyBid(state: SpadesState, seat: number, bid: number): SpadesState {
  if (state.phase !== 'bidding') throw new RuleError('Not bidding now');
  if (state.turn !== seat) throw new RuleError('Not your turn');
  if (!Number.isInteger(bid) || bid < 0 || bid > 13) throw new RuleError('Bid must be 0–13');
  const bids = state.bids.slice();
  bids[seat] = bid;
  const done = bids.every((b) => b !== null);
  return { ...state, bids, turn: done ? next(state.dealer) : next(seat), phase: done ? 'playing' : 'bidding' };
}

export function scoreHand(state: SpadesState): HandResult {
  const bids = state.bids.map((b) => b ?? 0);
  const tricks = state.tricksWon;
  const points: [number, number] = [0, 0];
  const bagsAdded: [number, number] = [0, 0];
  const nil: HandResult['nil'] = [];
  for (const team of [0, 1] as const) {
    const seats = [team, team + 2];
    let bid = 0, took = 0;
    for (const s of seats) {
      if (bids[s] === 0) {
        const made = tricks[s] === 0;
        nil.push({ seat: s, made });
        points[team] += made ? 100 : -100;
        if (!made) { points[team] += tricks[s]; bagsAdded[team] += tricks[s]; }
      } else {
        bid += bids[s];
        took += tricks[s];
      }
    }
    // (Both partners nil: no contract — only the nil results count.)
    if (bid > 0) {
      if (took >= bid) {
        points[team] += 10 * bid + (took - bid);
        bagsAdded[team] += took - bid;
      } else {
        points[team] -= 10 * bid;
      }
    }
  }
  return { bids, tricks: tricks.slice(), points, bagsAdded, bagPenalty: [false, false], nil };
}

export function applyPlay(state: SpadesState, hand: Card[], seat: number, card: Card): { state: SpadesState; hand: Card[] } {
  if (state.phase !== 'playing') throw new RuleError('Not playing now');
  if (state.turn !== seat) throw new RuleError('Not your turn');
  if (!hand.includes(card)) throw new RuleError('You do not hold that card');
  if (!legalPlays(state, hand).includes(card)) {
    throw new RuleError(state.trick.length === 0 ? 'Spades are not broken yet' : 'You must follow suit');
  }
  const newHand = hand.filter((c) => c !== card);
  const trick = [...state.trick, { seat, card }];
  const counts = state.counts.slice(); counts[seat] -= 1;
  let s: SpadesState = {
    ...state, trick, counts, played: [...state.played, card],
    spadesBroken: state.spadesBroken || suitOf(card) === 'S',
    turn: next(seat),
  };
  if (trick.length === 4) {
    const winner = trick[winningIndex(trick)].seat;
    const tricksWon = s.tricksWon.slice(); tricksWon[winner] += 1;
    s = { ...s, trick: [], lastTrick: { plays: trick, winner }, tricksWon, turn: winner };
    if (s.played.length === 52) s = finishHand(s);
  }
  return { state: s, hand: newHand };
}

function finishHand(s: SpadesState): SpadesState {
  const res = scoreHand(s);
  const scores: [number, number] = [s.scores[0] + res.points[0], s.scores[1] + res.points[1]];
  const bags: [number, number] = [s.bags[0] + res.bagsAdded[0], s.bags[1] + res.bagsAdded[1]];
  for (const t of [0, 1] as const) {
    while (bags[t] >= 10) { bags[t] -= 10; scores[t] -= 100; res.bagPenalty[t] = true; }
  }
  let winner: 0 | 1 | null = null;
  if (scores[0] <= FLOOR || scores[1] <= FLOOR) {
    if (scores[0] !== scores[1]) winner = scores[0] > scores[1] ? 0 : 1;
  }
  if (winner === null && (scores[0] >= TARGET || scores[1] >= TARGET) && scores[0] !== scores[1]) {
    winner = scores[0] > scores[1] ? 0 : 1;
  }
  return { ...s, scores, bags, lastHand: res, phase: winner === null ? 'hand_over' : 'game_over', winner };
}

// ---------------------------------------------------------------------------
// Computer players
// ---------------------------------------------------------------------------

const bySuit = (hand: Card[], suit: string) => hand.filter((c) => suitOf(c) === suit).sort((a, b) => rankOf(a) - rankOf(b));

/** Estimated tricks this hand can take (the usual high-card + length count). */
export function estimateTricks(hand: Card[]): number {
  const spades = bySuit(hand, 'S');
  const sr = new Set(spades.map((c) => c[0]));
  let t = 0;
  let spadeHonours = 0;
  if (sr.has('A')) { t += 1; spadeHonours++; }
  if (sr.has('K')) { t += spades.length >= 2 ? 1 : 0.4; spadeHonours++; }
  if (sr.has('Q')) { t += spades.length >= 3 ? 0.9 : 0.3; spadeHonours++; }
  if (sr.has('J') && spades.length >= 4) { t += 0.5; spadeHonours++; }
  t += Math.max(0, spades.length - 3) * 0.85;
  let spare = Math.max(0, spades.length - spadeHonours - Math.max(0, spades.length - 3));
  for (const suit of ['C', 'D', 'H']) {
    const cs = bySuit(hand, suit);
    const r = new Set(cs.map((c) => c[0]));
    if (r.has('A')) t += cs.length >= 6 ? 0.7 : 1;
    if (r.has('K')) t += cs.length >= 2 && cs.length <= 5 ? 0.75 : 0.25;
    if (r.has('Q') && cs.length >= 3 && cs.length <= 4) t += 0.3;
    if (cs.length === 0 && spare > 0) { t += Math.min(2, spare) * 0.7; spare -= Math.min(2, spare); }
    else if (cs.length === 1 && spare > 0) { t += 0.5; spare -= 1; }
  }
  return t;
}

export function botBid(state: SpadesState, hand: Card[], seat: number): number {
  const est = estimateTricks(hand);
  const spades = bySuit(hand, 'S');
  const high = (c: Card) => rankOf(c) >= RANKS.indexOf('Q');
  const partnerBid = state.bids[partnerOf(seat)];
  // Nil: weak hand, short low spades, no aces or high spades, few high cards.
  const sideAces = hand.filter((c) => c[0] === 'A' && suitOf(c) !== 'S').length;
  const highCount = hand.filter(high).length;
  const nilOk = est < 1.3 && spades.length <= 3 && !spades.some((c) => rankOf(c) >= RANKS.indexOf('J'))
    && sideAces === 0 && highCount <= 2 && (partnerBid === null || partnerBid >= 3);
  if (nilOk) return 0;
  let bid = Math.max(1, Math.round(est));
  // Don't overbid the table: the four bids rarely make more than 13.
  const others = state.bids.reduce<number>((s, b) => s + (b ?? 0), 0);
  if (others + bid > 13) bid = Math.max(1, 13 - others);
  return Math.min(13, bid);
}

/** Is `card` the highest one of its suit still out (not played, not in hand)? */
function isBoss(card: Card, played: Card[], hand: Card[]): boolean {
  const gone = new Set([...played, ...hand]);
  for (let r = rankOf(card) + 1; r < 13; r++) if (!gone.has(RANKS[r] + suitOf(card))) return false;
  return true;
}

export function botPlay(state: SpadesState, hand: Card[], seat: number): Card {
  const legal = legalPlays(state, hand);
  if (legal.length === 1) return legal[0];
  const low = (cs: Card[]) => cs.slice().sort((a, b) => rankOf(a) - rankOf(b) || (suitOf(a) === 'S' ? 1 : 0) - (suitOf(b) === 'S' ? 1 : 0))[0];
  const highest = (cs: Card[]) => cs.slice().sort((a, b) => rankOf(b) - rankOf(a))[0];
  const bids = state.bids.map((b) => b ?? 0);
  const team = teamOf(seat);
  const partner = partnerOf(seat);
  const myNil = bids[seat] === 0;
  const partnerNil = bids[partner] === 0;
  const teamBid = [team, team + 2].reduce((s, x) => s + (bids[x] === 0 ? 0 : bids[x]), 0);
  const teamTook = [team, team + 2].reduce((s, x) => s + (bids[x] === 0 ? 0 : state.tricksWon[x]), 0);
  const need = teamBid - teamTook;
  const tricksLeft = 13 - state.tricksWon.reduce((a, b) => a + b, 0);
  // Late in the game, with the contract safe, extra tricks are bags to avoid.
  const avoidBags = need <= 0 && !partnerNil;

  // Would this card win the trick as it stands now?
  const wins = (card: Card) => winningIndex([...state.trick, { seat, card }]) === state.trick.length;

  if (myNil) {
    // Never win: play the highest card that still loses, else the lowest.
    if (state.trick.length === 0) return low(legal);
    const losers = legal.filter((c) => !wins(c));
    return losers.length ? highest(losers) : low(legal);
  }

  if (state.trick.length === 0) {
    // Leading.
    if (avoidBags) return low(legal);
    const bosses = legal.filter((c) => suitOf(c) !== 'S' && isBoss(c, state.played, hand));
    if (bosses.length && need > 0) return highest(bosses);
    const spadeBoss = legal.filter((c) => suitOf(c) === 'S' && isBoss(c, state.played, hand));
    if (spadeBoss.length && need > 0 && state.spadesBroken) return spadeBoss[0];
    // Lead low from the longest side suit (keeps spades for trumping).
    const side = legal.filter((c) => suitOf(c) !== 'S');
    if (side.length) {
      const lengths: Record<string, number> = {};
      for (const c of hand) lengths[suitOf(c)] = (lengths[suitOf(c)] || 0) + 1;
      const longest = side.slice().sort((a, b) => lengths[suitOf(b)] - lengths[suitOf(a)] || rankOf(a) - rankOf(b));
      return longest[0];
    }
    return low(legal);
  }

  const cur = state.trick[winningIndex(state.trick)];
  const partnerWinning = cur.seat === partner;
  const lastToPlay = state.trick.length === 3;
  const winners = legal.filter(wins);
  const losers = legal.filter((c) => !wins(c));
  const cheapestWinner = winners.length ? low(winners) : null;

  if (partnerNil) {
    // Cover partner: if partner is winning (bad for a nil), take it over.
    if (partnerWinning && cheapestWinner) return lastToPlay ? cheapestWinner : highest(winners);
    if (!partnerWinning && state.trick.some((p) => p.seat === partner)) {
      return losers.length ? low(losers) : low(legal);
    }
    // Partner still to play: play high to win the trick before they must.
    if (winners.length) return highest(winners);
    return low(legal);
  }

  if (partnerWinning) {
    // Partner has it: duck, unless partner's card is weak and we're not last.
    const partnerCardSafe = lastToPlay || isBoss(cur.card, state.played, hand) || suitOf(cur.card) === 'S';
    if (partnerCardSafe || avoidBags) return losers.length ? (avoidBags ? highest(losers) : low(losers)) : low(legal);
    if (cheapestWinner && need > 0 && suitOf(cheapestWinner) !== 'S') return cheapestWinner;
    return losers.length ? low(losers) : low(legal);
  }

  // An opponent is winning.
  const oppNil = bids[cur.seat] === 0 && state.tricksWon[cur.seat] === 0;
  if (oppNil) {
    // Let a nil bidder win the trick — that busts their nil.
    return losers.length ? highest(losers) : low(legal);
  }
  if (avoidBags && tricksLeft > 1) return losers.length ? highest(losers) : low(legal);
  if (cheapestWinner) {
    const isTrump = suitOf(cheapestWinner) === 'S' && suitOf(state.trick[0].card) !== 'S';
    // Win cheaply when we're last, or with a card nobody can beat, or by trumping.
    if (lastToPlay || isTrump || isBoss(cheapestWinner, state.played, hand)) return cheapestWinner;
    // Second/third hand: play a solid high card if we need tricks.
    if (need > 0) return highest(winners);
  }
  // Can't (or shouldn't) win: shed the lowest card, keeping spades.
  const nonSpadeLosers = losers.filter((c) => suitOf(c) !== 'S');
  return low(nonSpadeLosers.length ? nonSpadeLosers : losers.length ? losers : legal);
}

// ---------------------------------------------------------------------------
// Table framework hookup (registered in registry.ts)
// ---------------------------------------------------------------------------

export type SpadesMove = { type: 'bid'; bid: number } | { type: 'play'; card: string };

export const spadesGame: GameDef<SpadesState, SpadesMove> = {
  kind: 'spades',
  label: 'Spades',
  minPlayers: 4,
  maxPlayers: 4,
  seatNames: SEAT_NAMES,
  start: (prev, ctx) => {
    const { state, hands } = deal(prev, ctx.rng);
    return { g: state, privs: { ...hands } };
  },
  turnSeat: (g) => (g.phase === 'bidding' || g.phase === 'playing' ? g.turn : null),
  move: (g, privs, seat, m) => {
    if (m?.type === 'bid') return { g: applyBid(g, seat, Number(m.bid)) };
    if (m?.type === 'play') {
      const r = applyPlay(g, (privs[seat] as Card[]) || [], seat, String(m.card));
      return { g: r.state, privs: { [seat]: r.hand } };
    }
    throw new RuleError('Unknown move');
  },
  bot: {
    move: (g, hand: Card[], seat) => g.phase === 'bidding'
      ? { type: 'bid', bid: botBid(g, hand, seat) }
      : { type: 'play', card: botPlay(g, hand, seat) },
    names: ['Ace Bot', 'Deuce Bot', 'Trey Bot', 'Jack Bot'],
  },
  betweenHands: (g) => g.phase === 'hand_over',
  betweenHandsMs: 7_000,
  isOver: (g) => g.phase === 'game_over',
  // Partnerships: both members of the winning team win; points = team score.
  results: (g) => [0, 1, 2, 3].map((seat): SeatResult => ({
    seat,
    outcome: g.winner === null ? 'draw' : teamOf(seat) === g.winner ? 'win' : 'loss',
    points: g.scores[teamOf(seat)],
  })),
  // A trick just closed: hold it on the table before the next lead.
  pauseAfter: (prev, g) => (g.lastTrick && prev.trick.length === 3 && g.trick.length === 0 ? 1600 : 0),
  describe: (m, who) => (m.type === 'bid' ? `${who} bid ${m.bid === 0 ? 'nil' : m.bid}` : `${who} played`),
};
